/** @module The admin screen: machine, jobs, scans, feedback, training, logs and recognition. */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/page-header";

import { FeedbackPanel } from "@/features/admin/feedback-panel";
import { JobsPanel } from "@/features/admin/jobs-panel";
import { LogsPanel } from "@/features/admin/logs-panel";
import { RecognitionPanel } from "@/features/admin/recognition-panel";
import { ScanList } from "@/features/admin/scan-list";
import { ScansPanel } from "@/features/admin/scans-panel";
import { SystemPanel } from "@/features/admin/system-panel";
import { TrainingPanel } from "@/features/admin/training-panel";
import { UsersPanel } from "@/features/admin/users-panel";
import { useOpenFeedback } from "@/lib/feedback-count";
import { useSession } from "@/lib/use-session";

export const TABS = [
  "system",
  "jobs",
  "recognition",
  "users",
  "scans",
  "feedback",
  "training",
  "logs",
] as const;
export type Tab = (typeof TABS)[number];

function isTab(value: unknown): value is Tab {
  return TABS.includes(value as Tab);
}

export function AdminPage() {
  const { t } = useTranslation();
  const session = useSession();
  // The tab lives in the address, so the sidebar and the "Mehr" page can link straight to it.
  const { tab: wanted } = useSearch({ strict: false });
  const navigate = useNavigate();
  const tab: Tab = isTab(wanted) ? wanted : "system";
  const unread = useOpenFeedback(session.account?.role === "admin");
  const setTab = (next: Tab) => {
    void navigate({ to: "/admin", search: { tab: next }, replace: true });
  };

  // The route guard checks this too; the screen says why rather than showing nothing.
  if (session.account?.role !== "admin") {
    return (
      <p role="alert" className="t-muted">
        {t("admin.forbidden")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("admin.title")} subtitle={t("admin.description")} global={false} />

      <div role="tablist" aria-label={t("admin.title")} className="tabs overflow-x-auto">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            onClick={() => {
              setTab(name);
            }}
            className="tab whitespace-nowrap"
          >
            {t(`admin.tabs.${name}`)}
            {name === "feedback" && unread > 0 ? (
              <span className="badge badge-count ml-1.5">{unread}</span>
            ) : null}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {tab === "system" ? <SystemPanel /> : null}
        {tab === "jobs" ? <JobsPanel /> : null}
        {tab === "scans" ? (
          <div className="flex flex-col gap-8">
            <ScansPanel />
            <ScanList />
          </div>
        ) : null}
        {tab === "feedback" ? <FeedbackPanel /> : null}
        {tab === "training" ? <TrainingPanel /> : null}
        {tab === "logs" ? <LogsPanel /> : null}
        {tab === "recognition" ? <RecognitionPanel /> : null}
        {tab === "users" ? <UsersPanel /> : null}
      </div>
    </div>
  );
}
